import React, { FC, useCallback, useState } from 'react';
import { NavLink, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Input, Nav } from 'reactstrap';
import { CurrentUser } from '../../../../shared/user/base';
import { isSpecialRole } from '../../../../shared/user/roles';
import { ElementHeader, PermissionBoundary } from '../../../components';
import { Permission, Role } from '../../../models';
import { useTranslation } from '../../../translations';
import { checkPermission, useForm } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';
import { AuditObjectPage } from '../audit/log';
import { PermissionEnum, PermissionListComponent } from './PermissionList';
import { RoleInvites } from './RoleInvites';
import { RoleUserList } from './RoleUserList';

interface RouterParams {
  id: string;
}

export interface RoleEditProps {
  readonly user: CurrentUser;
  readonly roles: Role[];
  readonly permissions: Permission[];
  readonly refresh: () => void;
}

export const RoleEdit: FC<RoleEditProps & RouteComponentProps<RouterParams>> = ({
  user,
  roles,
  permissions,
  refresh,
  match,
}) => {
  const { ts } = useTranslation();
  const calculatedId = Number.parseInt(match.params.id, 10);
  const role = roles.find((t) => t.id === calculatedId)!;

  const updateRole = useCallback(
    async (permissionId: number, state: PermissionEnum) => {
      try {
        if (state === PermissionEnum.Inherit) {
          await api.deletePermissionRole(calculatedId, permissionId);
        } else {
          await api.updatePermissionRole(calculatedId, permissionId, state === PermissionEnum.Deny);
        }
      } catch (error) {
        captureError(error as Error);
      }

      refresh();
    },
    [calculatedId, refresh],
  );

  return (
    <>
      <ElementHeader title={ts('managing_role', role.name)} />
      <Nav tabs>
        <NavLink activeClassName="active" className="nav-link" exact id="main_tab" to={match.url}>
          {ts('permissions')}
        </NavLink>
        <NavLink
          activeClassName="active"
          className="nav-link"
          id="users_tab"
          to={`${match.url}/users`}
        >
          {ts('users')}
        </NavLink>
        {!isSpecialRole(role.name) && (
          <NavLink
            activeClassName="active"
            className="nav-link"
            id="invites_tab"
            to={`${match.url}/invites`}
          >
            {ts('invites')}
          </NavLink>
        )}
        <PermissionBoundary inline requiredPermissions="audit:read">
          <NavLink
            activeClassName="active"
            className="nav-link"
            id="audit_tab"
            to={`${match.url}/auditlogs`}
          >
            {ts('history')}
          </NavLink>
        </PermissionBoundary>
        {checkPermission(user, 'system:permission:update') && (
          <NavLink
            activeClassName="active"
            className="nav-link"
            id="delete_tab"
            to={`${match.url}/delete`}
          >
            {ts('common.delete')}
          </NavLink>
        )}
      </Nav>
      <div id="roleEdit" style={{ paddingTop: '20px' }}>
        <Switch>
          <Route exact path={match.path}>
            <PermissionListComponent
              canEdit={checkPermission(user, 'system:permission:update')}
              items={role.rolePermissions}
              permissions={permissions}
              update={async (a, b) => {
                await updateRole(a, b);
              }}
            />
          </Route>
          <Route path={`${match.path}/users`}>
            <RoleUserList role={role} />
          </Route>
          {!isSpecialRole(role.name) && (
            <Route path={`${match.path}/invites`}>
              <RoleInvites role={role} />
            </Route>
          )}
          <Route path={`${match.path}/delete`}>
            <RoleDelete role={role} />
          </Route>
          <Route path={`${match.path}/auditlogs`}>
            <AuditObjectPage entityId="Role" objectId={role.id.toString()} />
          </Route>
        </Switch>
      </div>
    </>
  );
};

const RoleDelete: FC<{ readonly role: Role }> = ({ role }) => {
  const [input, setInput] = useState('');
  const { ts } = useTranslation();

  const form = useForm(async () => {
    if (role.name !== input) {
      toast.error('The input does not match the role name.');
      return;
    }

    await api.deleteRole(role.id);

    window.location.replace('/housekeeping/roles');
  }, [input, role]);

  return (
    <form onSubmit={form.onSubmit}>
      <h3>{ts('warning_you_are_about_to_delete_role', role.name)}</h3>
      <div>
        Please input '{role.name}' on the textbox.
        <Input
          id="deleteRoleCheck"
          onChange={(e) => {
            setInput(e.target.value);
          }}
          value={input}
        />
        <Button color="danger" id="deleteRole">
          {ts('common.delete')}
        </Button>
      </div>
    </form>
  );
};
